import { RetailPaymentReferenceTypeEnum, RetailTransaction } from '@galaxy/billing';

const INVOICE_PREFIX = 'Invoice: ';
const SALES_ORDER_PREFIX = 'Order: ';

/**
 * This is responsible for removing the description prefix on a list of payments. If the reference is an invoice, the
 * "Invoice: " prefix will be removed. If the reference is an order the "Order: " prefix will be removed.
 *
 * @param payments as list of payments.
 * @return a list of payments without the description prefix.
 */
export function stripReferencePrefixFromPaymentDescriptions(payments: RetailTransaction[]): RetailTransaction[] {
  return payments?.map((p) => {
    if (p.payment) {
      p.payment.description = stripReferencePrefixFromPaymentDescription(
        p.payment.description,
        p.payment.referenceType,
      );
    }
    if (p.refund?.payment) {
      p.refund.payment.description = stripReferencePrefixFromPaymentDescription(
        p.refund.payment.description,
        p.refund.payment.referenceType,
      );
    }
    return p;
  });
}

/**
 * Strip the reference prefix from a payment description based on its type.
 *
 * Many of the payment descriptions are prefixed with "Invoice: " or "Order: " based on what the payment was applied to.
 * In order to display these values nicer in the UI, it may be desired to remove these prefixes.
 *
 * @param description
 * @param type
 */
export function stripReferencePrefixFromPaymentDescription(
  description: string,
  type: RetailPaymentReferenceTypeEnum,
): string {
  const prefix = getPrefixFromReferenceType(type);
  if (description.startsWith(prefix)) {
    return description.slice(prefix.length);
  }
  return description;
}

/**
 * Returns the common prefixes different descriptions many have.
 *
 * An invoice is usually prefixed with "Invoice: "
 * A sales order is usually prefixed with "Order: "
 *
 * If the type does not have a prefix, an empty string will be returned.
 */
function getPrefixFromReferenceType(type: RetailPaymentReferenceTypeEnum): string {
  switch (type) {
    case RetailPaymentReferenceTypeEnum.RETAIL_PAYMENT_REFERENCE_TYPE_INVOICE:
      return INVOICE_PREFIX;
    case RetailPaymentReferenceTypeEnum.RETAIL_PAYMENT_REFERENCE_TYPE_SALES_ORDER:
      return SALES_ORDER_PREFIX;
    default:
      return '';
  }
}
